/**
 * royaleConstants.ts
 *
 * Single source of truth for every tunable in Puzzle Royale — the N-player
 * Fun Challenge. The PRD requires all constants to be configurable, so they
 * live here (frontend) and are mirrored by `funPuzzleRoyale.getDefaultSettings()`
 * (cc-game-service) and the Django sequence/result defaults.
 *
 * Design / scoring physics are sourced from the authoritative design bundle:
 *   src/Screens/FunChallenges/docs/design/project/Puzzle Race - Live Strip.html
 *   src/Screens/FunChallenges/docs/design/chat1.md (intent transcript)
 *
 * Capacity model (v2): the ENGINE handles up to 100 players. 10 is only a
 * configurable PUBLIC / CLASSROOM display + auto-start cap, NOT an engine
 * limit — a privileged caller can raise maxPlayers up to ENGINE_MAX_PLAYERS.
 */

import type {PieceTier, RoyaleSettings} from './royaleTypes';

// ── Capacity ──────────────────────────────────────────────────────────────
/** Hard engine ceiling — store, broadcast, leaderboard all run this many. */
export const ENGINE_MAX_PLAYERS = 100;
/** Public + classroom entry cap. Configurable; NOT an engine limit. */
export const DISPLAY_CAP = 10;
/** Minimum joined players the host needs before the manual Start unlocks. */
export const MIN_TO_START = 2;
/** Lobby auto-starts the moment the joined count hits this (= the display cap). */
export const AUTO_START_AT = DISPLAY_CAP;
/** Race strip always shows this many top rows + a fixed "you" row. */
export const LEADERBOARD_VISIBLE = 10;
/** App (future) shows you + this many others (top 3 if you're rank 1/2). */
export const APP_VISIBLE_ROWS = 3;

// ── Lobby ───────────────────────────────────────────────────────────────────
/** A lobby expires this long after creation; late joiners are told to recreate. */
export const LOBBY_EXPIRY_MS = 15 * 60 * 1000;
/** Pre-game 3-2-1 countdown, in seconds (mirrors the server COUNTDOWN_SEC). */
export const COUNTDOWN_SEC = 3;

// ── Time control (host slider) ────────────────────────────────────────────
/** Total race duration options, in seconds. Slider snaps to these. */
export const TIME_CHIPS = [30, 60, 90, 120, 180, 300] as const;
export const TIME_DEFAULT = 90;

// ── Topics (host) ─────────────────────────────────────────────────────────
/**
 * Topics are OPTIONAL — an empty selection means "no topic filter" (any
 * theme). When the host does pick, they may select up to this many. The
 * topic list itself lives in royaleTopics.ts (boolean theme columns).
 */
export const MAX_TOPICS = 4;

// ── Difficulty (host) ─────────────────────────────────────────────────────
export type Difficulty = 'easy' | 'medium' | 'hard';
export const DIFFICULTY_OPTIONS: Difficulty[] = ['easy', 'medium', 'hard'];
export const DIFFICULTY_DEFAULT: Difficulty = 'easy';
/**
 * Difficulty → player-move-count band (theme-independent; NOT mate columns).
 * Derived server-side from the `moves` string length: easy = 1-2 player moves,
 * medium = 3, hard = 4+. Kept here only as documentation for the UI labels.
 */
export const DIFFICULTY_MOVE_LABELS: Record<Difficulty, string> = {
  easy: '1–2 moves',
  medium: '3 moves',
  hard: '4+ moves',
};

// ── Punish mistakes (host) ────────────────────────────────────────────────
export const PUNISH_DEFAULT = false;
/** When punish is on, how many wrong puzzles eliminate a player from scoring. */
export const PUNISH_THRESHOLDS = [5, 10] as const;
export const PUNISH_DEFAULT_THRESHOLD = 5;

// ── Scoring: streak ladder ──────────────────────────────────────────────────
/**
 * Streak bonus TIERS. A correct solve is always +1 point; PLUS a per-solve
 * bonus equal to the highest tier whose threshold ≤ the current combo — applied
 * on EVERY consecutive solve while the streak is up, not just once at the
 * threshold (PRD update 2026-06-14). A wrong solve resets the combo to 0 (no
 * point). Tiers (combo ≥ `at` → `bonus` per solve):
 *   combo 3–4 → +1, 5–9 → +2, 10–14 → +3, 15+ → +5.
 * Consumed by royaleScoring.comboTierBonus (client) + recordRoyaleScore (server).
 */
export const STREAK_LADDER: Array<{at: number; bonus: number}> = [
  {at: 3, bonus: 1},
  {at: 5, bonus: 2},
  {at: 10, bonus: 3},
  {at: 15, bonus: 5},
];

// ── Piece rank (points → piece) ─────────────────────────────────────────────
/**
 * Point bands → piece tier. King is NOT a band here — it's the LEADER badge:
 * the current points leader's moving piece renders as a King once any player
 * reaches KING_ENABLED_AT, transferring live as the lead changes (see
 * royaleScoring.resolveKing). PRD bands: Pawn 0-2 / Knight 3-5 / Bishop 6-9
 * / Rook 10-14 / Queen 15+.
 */
export const PIECE_BANDS: Array<{tier: PieceTier; min: number}> = [
  {tier: 'pawn', min: 0},
  {tier: 'knight', min: 3},
  {tier: 'bishop', min: 6},
  {tier: 'rook', min: 10},
  {tier: 'queen', min: 15},
];
/** King (leader badge) only lights up once SOME player has reached this. */
export const KING_ENABLED_AT = 3;

// ── Player colours ──────────────────────────────────────────────────────────
/**
 * When true, each of the (up to DISPLAY_CAP) players gets a DISTINCT colour
 * assigned by lobby JOIN ORDER, and the local user ("you") is ALWAYS green so
 * a player consistently sees themselves in green. When false, fall back to the
 * legacy per-userId hash (colorForUser) — whatever was present before.
 */
export const DIFFERENT_COLOR = true;
/** The colour the local user always renders as when DIFFERENT_COLOR is on. */
export const SELF_COLOR = '#1A7E73'; // green
/**
 * Distinct accents handed out by join order to the OTHER players (green is
 * reserved for "you", so it's excluded here). Deeper colours, legible on the
 * cream/white surfaces (from the Live Strip design). Up to DISPLAY_CAP-1 others
 * get a unique one; beyond that they cycle.
 */
export const PLAYER_COLOR_SEQUENCE = [
  '#9A7600',
  '#C05828',
  '#6845B0',
  '#2878C0',
  '#B03888',
  '#985018',
  '#A81818',
  '#2858A8',
  '#1A7535',
];

// ── Race-strip physics (from the Live Strip design) ─────────────────────────
/** Leader sits here (%) until someone crosses LEADER_X_THRESHOLD points… */
export const LEADER_X_PRE = 50;
/** …then the leader pushes to here (%). */
export const LEADER_X_POST = 85;
/** Point total at which the track opens up (leader → LEADER_X_POST). */
export const LEADER_X_THRESHOLD = 3;
/** Base horizontal spread: % per point of gap behind the leader. */
export const X_SCALE_PER_PT = 2;
/** Last place never falls below this (%) — scale compresses to keep it visible. */
export const MIN_X = 5;

// ── Sequence ────────────────────────────────────────────────────────────────
/** Puzzles pre-rolled per race. Generous (5-min races can be ~100 puzzles). */
export const SEQUENCE_LENGTH = 100;

// ── In-race controls ────────────────────────────────────────────────────────
/**
 * Show a "Leave race" (forfeit) control during a live race. When true, a
 * player can quit mid-race — the server marks them out (non-terminating; the
 * race continues for everyone else) and the client returns to the lobby. Set
 * false to hide the option (e.g. classroom races where the coach controls the
 * session).
 */
export const SHOW_LEAVE_RACE = true;

// ── Classroom ───────────────────────────────────────────────────────────────
/** Students auto-join the moment they tap "Join Puzzle Royale". */
export const CLASSROOM_AUTO_JOIN = true;
/** Classroom royales NEVER auto-start — only the coach can start. */
export const CLASSROOM_AUTO_START = false;

// ── Defaults ────────────────────────────────────────────────────────────────
/**
 * The host-settings object a freshly-opened Create modal starts from. Topics
 * is intentionally empty — the host MUST pick at least one (PRD: no default,
 * but cannot start empty; the Create button stays disabled until non-empty).
 */
export const DEFAULT_ROYALE_SETTINGS: RoyaleSettings = {
  topics: [],
  timerSec: TIME_DEFAULT,
  difficulty: DIFFICULTY_DEFAULT,
  punish: PUNISH_DEFAULT,
  punishThreshold: PUNISH_DEFAULT_THRESHOLD,
  minPlayers: MIN_TO_START,
  maxPlayers: DISPLAY_CAP,
  autoStartAt: AUTO_START_AT,
  lobbyExpiryMs: LOBBY_EXPIRY_MS,
};
